import { useEffect, useState } from "react";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { periodOverPeriodService } from "app/services/PeriodOverPeriodService";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";
import { PREVIOUS_YEAR, PREVIOUS_PERIOD } from "app/services/periodOverPeriodTypes";
import { allTrackers } from "app/services/track/track";

import type { PeriodOverPeriodCompareTypes } from "./types";

const durationFormatter = (duration: string, comparedDuration: string) => {
    const durationObject = DurationService.getDurationData(duration, comparedDuration);
    const { forWidget } = durationObject;
    const [currentPeriod, previousPeriod] = forWidget;
    return `${previousPeriod} ${translate("common.vs")} ${currentPeriod}`;
};

const getDescription = (isDisabled: boolean, duration: string, comparedDuration: string) => {
    return isDisabled
        ? translate("period.over.period.toggle.menu.disabled")
        : durationFormatter(duration, comparedDuration);
};

const LABELS = {
    [PREVIOUS_YEAR]: "common.yoy",
    [PREVIOUS_PERIOD]: "common.pop",
};

export const usePeriodOverPeriod = () => {
    const isPoP = DurationService.isPeriodOverPeriod();
    const [periodOverPeriodCompareType, setPeriodOverPeriodCompareType] =
        useState<IPeriodOverPeriodCompareType>(PREVIOUS_YEAR);
    const { duration, comparedDuration: comparedDurationParam } = proNavigator.getParams();
    const componentId = swSettings.current.componentId;
    const state = proNavigator.current();
    const { from, to } = DurationService.getDurationData(duration).forAPI;
    // @ts-ignore FIXME: getDiffSymbol expects Dayjs but forAPI.from/to are strings — callers should use raw (Dayjs) dates instead
    const comparedDuration = DurationService.getDiffSymbol(from, to);

    const isPeriodOverPeriodDisabled =
        !periodOverPeriodService.periodOverPeriodEnabled(duration, duration, "", componentId, comparedDuration) ||
        !state.periodOverPeriodEnabled ||
        duration === BasicDurations.LAST_TWELVE_MONTHS;
    const isYearOverYearDisabled =
        !periodOverPeriodService.periodOverPeriodEnabled(
            duration,
            BasicDurations.LAST_TWELVE_MONTHS,
            "",
            componentId,
        ) || !state.periodOverPeriodEnabled;

    const periodOverPeriodCompareTypes: PeriodOverPeriodCompareTypes = [
        {
            id: PREVIOUS_YEAR,
            title: translate("period.over.period.toggle.menu.yoy"),
            description: getDescription(isYearOverYearDisabled, duration, BasicDurations.LAST_TWELVE_MONTHS),
            isDisabled: isYearOverYearDisabled,
            isSelected: isPoP && periodOverPeriodCompareType === PREVIOUS_YEAR,
        },
        {
            id: PREVIOUS_PERIOD,
            title: translate("period.over.period.toggle.menu.pop"),
            description: getDescription(isPeriodOverPeriodDisabled, duration, comparedDuration),
            isDisabled: isPeriodOverPeriodDisabled,
            isSelected: isPoP && periodOverPeriodCompareType === PREVIOUS_PERIOD,
        },
    ];

    const onClick = () => {
        allTrackers.dispatchTrackEvent("toggle", "click", `Period over period toggle`, {
            isPeriodOverPeriodToggleOn: !isPoP,
        });
        if (isPoP) {
            return DurationService.clearPeriodOverPeriod();
        }
        DurationService.setPeriodOverPeriod(periodOverPeriodCompareType);
    };

    const onSelectCompareType = ({ id }: { id: IPeriodOverPeriodCompareType }) => {
        allTrackers.dispatchTrackEvent("dropdown", "click", "Period over period type", {
            selectedType: id,
        });
        DurationService.setPeriodOverPeriod(id);
        setPeriodOverPeriodCompareType(id);
    };

    useEffect(() => {
        const periodOverPeriodCompareType = DurationService.getPeriodOverPeriodCompareType();
        if (!periodOverPeriodCompareType) {
            if (!isPeriodOverPeriodDisabled && isYearOverYearDisabled) {
                setPeriodOverPeriodCompareType(PREVIOUS_PERIOD);
            }
            return;
        }
        setPeriodOverPeriodCompareType(periodOverPeriodCompareType);
    }, [duration, comparedDurationParam]);

    return {
        isPoP,
        isDisabled: isPeriodOverPeriodDisabled && isYearOverYearDisabled,
        labelKey: LABELS[periodOverPeriodCompareType],
        periodOverPeriodCompareTypes,
        onClick,
        onSelectCompareType,
    };
};
